import React from 'react';
import { useTranslation } from 'react-i18next';
import AssistantHistoryClearAllAlert from '../../../../components/Alert/Assistant/AssistantHistoryClearAllAlert.component';
import TrashIcon from '../../../../components/Icons/Trash.icon';
import TooltipComponent from '../../../../components/Tooltip.component';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../../hooks/useAppSelector.hook';
import { useModalManager } from '../../../../hooks/ModalManager.hook';
import {
  getAssistantHistoryClearing,
  getAssistantIsLoading,
  getAssistantMessages,
} from '../../../../redux/Assistant/Assistant.slice';
import { clearAssistantHistoryThunk } from '../../../../redux/Assistant/Assistant.thunk';
import { ALERT_ACTION_ENUM } from '../../../../types/action.types';
import {
  StyledAssistantClearAllButton,
  StyledAssistantClearAllIconButton,
  StyledAssistantTooltipWrapper,
} from '../Assistant.styles';

interface AssistantClearAllButtonProps {
  className?: string;
  variant?: 'text' | 'icon';
}

const AssistantClearAllButton = ({
  className,
  variant = 'text',
}: AssistantClearAllButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const messages = useAppSelector(getAssistantMessages);
  const isLoading = useAppSelector(getAssistantIsLoading);
  const isClearing = useAppSelector(getAssistantHistoryClearing);
  const disabled = messages.length === 0 || isLoading || isClearing;

  const handleConfirm = (action?: ALERT_ACTION_ENUM) => {
    if (!action || action !== ALERT_ACTION_ENUM.YES) {
      return;
    }

    dispatch(clearAssistantHistoryThunk());
  };

  const openClearConfirm = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (disabled) {
      return;
    }

    openModal(AssistantHistoryClearAllAlert, {
      handleConfirm,
    });
  };

  if (variant === 'icon') {
    return (
      <StyledAssistantTooltipWrapper className={className}>
        <StyledAssistantClearAllIconButton
          type="button"
          disabled={disabled}
          aria-label={t('assistant.clearAll')}
          onClick={openClearConfirm}
        >
          <TrashIcon width={14} height={14} />
        </StyledAssistantClearAllIconButton>
        <TooltipComponent tooltip={t('assistant.clearAll')} />
      </StyledAssistantTooltipWrapper>
    );
  }

  return (
    <StyledAssistantClearAllButton
      type="button"
      className={className}
      disabled={disabled}
      onClick={openClearConfirm}
    >
      {t('assistant.clearAll')}
    </StyledAssistantClearAllButton>
  );
};

export default AssistantClearAllButton;
